import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiUsers, FiArrowRight, FiBook, FiHelpCircle, FiFolder, FiPlay, FiPrinter, FiAlertCircle, FiActivity, FiAward, FiCheckSquare, FiZap, FiUserPlus } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, GOLD_GRADIENT, CAST, Page, PageHeader, Card, Tag, Alert, Button, Initials, ProgressBar, fadeUp, stagger } from '../common/ui';
import { PerlaOwl } from '../tutor/TutorOwl';

const HERO = { img: '/illustrations/lesson1/scenes/meet-team.jpg', pos: 'center 22%', height: 680, opacity: 0.6 };

const pad2 = (n) => String(n).padStart(2, '0');
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };
const ago = (d) => {
  if (!d) return 'never';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(m / 60), day = Math.floor(h / 24);
  if (m < 1) return 'just now'; if (m < 60) return `${m} min ago`; if (h < 24) return `${h} h ago`; if (day === 1) return 'yesterday'; if (day < 30) return `${day} days ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const sectionTitle = (icon, text, right) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
    <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '1.05rem', fontWeight: 900, margin: 0, color: BRAND.ivory }}>
      <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(205,155,80,0.16)', border: `1px solid ${BRAND.line}`, display: 'grid', placeItems: 'center', color: BRAND.goldLight }}>{icon}</span>{text}
    </h2>
    {right}
  </div>
);

function Kpi({ icon, value, label, hint, accent }) {
  return (
    <Card style={{ padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'center', background: accent ? 'linear-gradient(135deg, rgba(205,155,80,0.22), rgba(11,26,58,0.72))' : undefined }}>
      <span style={{ width: 46, height: 46, borderRadius: 14, background: GOLD_GRADIENT, color: BRAND.navy900, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <b style={{ fontSize: '1.6rem', lineHeight: 1, display: 'block' }}>{value}</b>
        <span style={{ fontSize: '0.72rem', color: BRAND.muted, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 800 }}>{label}</span>
        {hint && <div style={{ fontSize: '0.78rem', color: BRAND.faint, marginTop: 2 }}>{hint}</div>}
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try { const { data: res } = await api.get('/admin/dashboard'); if (!cancelled) setData(res); }
      catch (e) { if (!cancelled) { setError(e.response?.data?.error || 'Unable to load the dashboard'); setData(null); } }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = data?.stats || {};
  const course = data?.courseStats?.[0] || null;
  const lessons = data?.lessonProgress || [];
  const classLesson = data?.classLesson || null;
  const attention = data?.attention || [];
  const activity = data?.recentActivity || [];
  const leaders = data?.leaderboard || [];
  const students = stats.studentCount || 0;
  const lessonTotal = lessons.length || course?.lessonCount || 18;

  const nextStep = useMemo(() => {
    if (!data) return null;
    if (students === 0) return {
      who: 'trace', title: 'Your classroom is ready — add your students.',
      text: 'Create the accounts (or import a whole class from a list) and hand out the access codes. Then open Lesson 1 together on the big screen.',
      actions: [{ to: '/admin/studenti', label: 'Add students', icon: FiUserPlus }, classLesson && { to: `/admin/lezioni/${classLesson._id}`, label: 'Preview Lesson 1', icon: FiPlay, variant: 'outline' }].filter(Boolean),
    };
    if (attention.length) return {
      who: 'mina', title: `${attention.length} student${attention.length > 1 ? 's' : ''} could use a nudge.`,
      text: 'Some students have not logged in for a while or are struggling with the quizzes. A quick word in class usually brings them back.',
      actions: [{ to: '/admin/studenti', label: 'See the class', icon: FiUsers }, classLesson && { to: `/admin/lezioni/${classLesson._id}`, label: `Open Lesson ${classLesson.order + 1}`, icon: FiPlay, variant: 'outline' }].filter(Boolean),
    };
    if (classLesson) return {
      who: 'trace', title: `The class is on Lesson ${classLesson.order + 1} — ${classLesson.title}.`,
      text: `${classLesson.completed} of ${students} students have completed it. Open the deck to run it in class, or print the concept guide to prepare.`,
      actions: [{ to: `/admin/lezioni/${classLesson._id}`, label: 'Open the lesson', icon: FiPlay }, { href: `/resources/lessons/lesson-${pad2(classLesson.order + 1)}-concepts.pdf`, label: 'Concept guide (PDF)', icon: FiPrinter, variant: 'outline' }],
    };
    return null;
  }, [data, students, attention, classLesson]);

  const firstName = (user?.name || '').split(' ')[0];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <Page hero={HERO}>
      <PageHeader
        kicker={user?.school?.name ? `Teacher dashboard · ${user.school.name}` : 'Teacher dashboard'}
        title={<>{greeting()}{firstName ? `, ${firstName}` : ''}.</>}
        subtitle={students ? `${today} — here is how your class is doing.` : `${today} — welcome to CORE for Schools.`}
        right={classLesson && students > 0 ? (
          <Button to={`/admin/lezioni/${classLesson._id}`}><FiPlay size={16} /> Lesson {classLesson.order + 1}</Button>
        ) : null}
      />

      {loading && <Card style={{ overflow: 'hidden' }}><LoadingSpinner message="Loading the dashboard…" /></Card>}
      {!loading && error && <Alert>{error}</Alert>}

      {!loading && !error && data && (
        <>
          {/* next step */}
          {nextStep && (
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <Card style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', padding: '18px 22px', borderColor: 'rgba(205,155,80,0.45)', background: 'linear-gradient(135deg, rgba(205,155,80,0.16), rgba(11,26,58,0.78) 55%)' }}>
                <img src={CAST[nextStep.who].img} alt={CAST[nextStep.who].name} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${BRAND.gold}`, background: BRAND.navy700, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: BRAND.goldLight }}>Next step · {CAST[nextStep.who].name} suggests</div>
                  <div style={{ fontWeight: 900, fontSize: '1.2rem', margin: '4px 0 4px' }}>{nextStep.title}</div>
                  <div style={{ color: BRAND.muted, fontSize: '0.92rem', lineHeight: 1.5 }}>{nextStep.text}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {nextStep.actions.map((a) => (
                    <Button key={a.label} to={a.to} href={a.href} target={a.href ? '_blank' : undefined} rel={a.href ? 'noreferrer' : undefined} variant={a.variant || 'gold'}><a.icon size={15} /> {a.label}</Button>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}

          {/* KPIs */}
          <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginTop: 18 }}>
            <motion.div variants={fadeUp}><Kpi icon={<FiUsers size={20} />} value={students} label="students" hint={students ? `${stats.activeStudents7d || 0} active this week` : 'none yet'} /></motion.div>
            <motion.div variants={fadeUp}><Kpi icon={<FiCheckSquare size={20} />} value={students ? `${stats.avgLessonsDone ?? 0} / ${lessonTotal}` : `0 / ${lessonTotal}`} label="avg lessons done" hint="per student" /></motion.div>
            <motion.div variants={fadeUp}><Kpi icon={<FiHelpCircle size={20} />} value={stats.avgQuizScore != null ? `${stats.avgQuizScore}%` : '—'} label="avg quiz score" hint={stats.avgQuizScore != null ? (stats.avgQuizScore >= 70 ? 'solid' : 'worth a review') : 'no attempts yet'} /></motion.div>
            <motion.div variants={fadeUp}><Kpi icon={<FiAward size={20} />} value={stats.badgesTotal || 0} label="badges earned" hint="by the whole class" /></motion.div>
          </motion.div>

          {/* main grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(300px, 1fr)', gap: 18, marginTop: 22 }} className="core-dash-grid">
            <style>{`@media (max-width: 960px){ .core-dash-grid{ grid-template-columns: 1fr !important; } }`}</style>

            {/* left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
              <motion.section variants={fadeUp} initial="hidden" animate="visible">
                {sectionTitle(<FiActivity size={16} />, 'Class progress', <Link to="/admin/corsi" style={{ color: BRAND.goldLight, fontWeight: 800, fontSize: '0.84rem', textDecoration: 'none' }}>All lessons →</Link>)}
                <Card style={{ padding: '14px 18px' }}>
                  {lessons.length === 0 && <p style={{ color: BRAND.muted, margin: 0 }}>Lessons will appear here once the course is active.</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', columnGap: 26, rowGap: 6 }}>
                    {lessons.map((l) => {
                      const pct = students ? Math.round((l.completed / students) * 100) : 0;
                      const current = classLesson && String(classLesson._id) === String(l._id);
                      return (
                        <Link key={l._id} to={`/admin/lezioni/${l._id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'grid', gridTemplateColumns: '30px 1fr 44px', alignItems: 'center', gap: 10, padding: '5px 6px', borderRadius: 10, background: current ? 'rgba(205,155,80,0.12)' : 'transparent' }}>
                          <span style={{ fontWeight: 900, color: current ? BRAND.goldLight : BRAND.faint, fontSize: '0.8rem' }}>{pad2(l.order + 1)}</span>
                          <span style={{ minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: current ? BRAND.ivory : 'rgba(249,240,231,0.86)' }}>{l.title}</span>
                            <ProgressBar value={pct} height={5} style={{ marginTop: 4 }} />
                          </span>
                          <span style={{ fontSize: '0.76rem', fontWeight: 800, color: BRAND.muted, textAlign: 'right' }}>{students ? `${l.completed}/${students}` : '—'}</span>
                        </Link>
                      );
                    })}
                  </div>
                </Card>
              </motion.section>

              <motion.section variants={fadeUp} initial="hidden" animate="visible">
                {sectionTitle(<FiZap size={16} />, 'Recent activity')}
                <Card style={{ padding: '8px 18px' }}>
                  {activity.length === 0 && <p style={{ color: BRAND.muted, margin: '10px 0', fontSize: '0.92rem' }}>Nothing yet — as soon as students complete lessons, quizzes or earn badges, it shows up here.</p>}
                  {activity.map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: i ? '1px solid rgba(249,240,231,0.07)' : 'none' }}>
                      <Initials name={e.student} size={32} />
                      <div style={{ flex: 1, minWidth: 0, fontSize: '0.9rem' }}>
                        <b>{e.student}</b>{' '}
                        {e.type === 'lesson' && <>completed <b style={{ color: BRAND.goldLight }}>Lesson {e.order != null ? e.order + 1 : ''}</b> — {e.title}</>}
                        {e.type === 'quiz' && <>took the quiz <b style={{ color: BRAND.goldLight }}>{e.title}</b>{e.pct != null && <Tag tone={e.pct >= 70 ? 'success' : 'danger'} style={{ marginLeft: 8 }}>{e.pct}%</Tag>}</>}
                        {e.type === 'badge' && <>earned the badge <b style={{ color: BRAND.goldLight }}>{e.icon} {e.title}</b></>}
                      </div>
                      <span style={{ fontSize: '0.76rem', color: BRAND.faint, whiteSpace: 'nowrap' }}>{ago(e.at)}</span>
                    </div>
                  ))}
                </Card>
              </motion.section>
            </div>

            {/* right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
              <motion.section variants={fadeUp} initial="hidden" animate="visible">
                {sectionTitle(<FiAlertCircle size={16} />, 'Needs attention')}
                <Card style={{ padding: '10px 16px' }}>
                  {attention.length === 0 && <p style={{ color: BRAND.muted, margin: '8px 0', fontSize: '0.9rem' }}>{students ? 'Everyone is on track. 🎉' : 'Once students start, the ones falling behind appear here.'}</p>}
                  {attention.map((s, i) => (
                    <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderTop: i ? '1px solid rgba(249,240,231,0.07)' : 'none' }}>
                      <Initials name={s.name} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                        <div style={{ fontSize: '0.74rem', color: BRAND.muted }}>{s.lessonsDone}/{lessonTotal} lessons · {s.quizAvg != null ? `${s.quizAvg}% quiz` : 'no quiz'} · {ago(s.lastActivity)}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-end' }}>
                        {s.reasons.includes('never-logged-in') && <Tag tone="muted">never in</Tag>}
                        {s.reasons.includes('inactive') && <Tag tone="danger">inactive</Tag>}
                        {s.reasons.includes('low-quiz') && <Tag tone="danger">low quiz</Tag>}
                      </div>
                    </div>
                  ))}
                  {students > 0 && <Link to="/admin/studenti" style={{ display: 'block', marginTop: 8, color: BRAND.goldLight, fontWeight: 800, fontSize: '0.84rem', textDecoration: 'none' }}>Open the class →</Link>}
                </Card>
              </motion.section>

              {leaders.length > 0 && leaders.some((l) => l.points > 0) && (
                <motion.section variants={fadeUp} initial="hidden" animate="visible">
                  {sectionTitle(<FiAward size={16} />, 'Top of the class')}
                  <Card style={{ padding: '10px 16px' }}>
                    {leaders.map((s, i) => (
                      <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i ? '1px solid rgba(249,240,231,0.07)' : 'none' }}>
                        <span style={{ width: 22, fontWeight: 900, color: i === 0 ? BRAND.goldLight : BRAND.faint }}>{i + 1}</span>
                        <Initials name={s.name} size={28} />
                        <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                        <span style={{ fontSize: '0.78rem', color: BRAND.muted }}>{s.badges} 🏅</span>
                        <b style={{ color: BRAND.goldLight, fontSize: '0.9rem' }}>{s.points} pts</b>
                      </div>
                    ))}
                  </Card>
                </motion.section>
              )}

              <motion.section variants={fadeUp} initial="hidden" animate="visible">
                {sectionTitle(<FiBook size={16} />, 'Your course')}
                <Card style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{ width: 42, height: 42, borderRadius: 12, background: GOLD_GRADIENT, display: 'grid', placeItems: 'center', fontSize: '1.2rem' }}>{course?.icon || '🦉'}</span>
                    <div>
                      <div style={{ fontWeight: 900 }}>{course?.title || 'CORE for Schools'}</div>
                      <div style={{ fontSize: '0.74rem', color: BRAND.muted }}>{course?.lessonCount ?? 18} lessons · {course?.quizCount ?? 18} quizzes · {course?.gameCount ?? 3} games</div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { to: '/admin/corsi', label: 'Lessons', icon: FiBook },
                      { to: '/admin/quiz', label: 'Quizzes', icon: FiHelpCircle },
                      { to: '/admin/giochi', label: 'Games', icon: FiPlay },
                      { to: '/admin/risorse', label: 'Teacher PDFs', icon: FiFolder },
                    ].map((l) => (
                      <Link key={l.to} to={l.to} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 12, background: 'rgba(249,240,231,0.06)', border: '1px solid rgba(249,240,231,0.1)', color: BRAND.ivory, textDecoration: 'none', fontWeight: 700, fontSize: '0.86rem' }}>
                        <l.icon size={15} style={{ color: BRAND.goldLight }} />{l.label}<FiArrowRight size={13} style={{ marginLeft: 'auto', color: BRAND.faint }} />
                      </Link>
                    ))}
                  </div>
                  <a href="/resources/CORE7_Instructor_Manual.pdf" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: BRAND.goldLight, fontWeight: 800, fontSize: '0.84rem', textDecoration: 'none' }}><FiPrinter size={14} /> Instructor manual (PDF)</a>
                </Card>
              </motion.section>

              <motion.section variants={fadeUp} initial="hidden" animate="visible">
                <Card style={{ padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'center', background: 'linear-gradient(135deg, rgba(205,155,80,0.18), rgba(11,26,58,0.72) 60%)' }}>
                  <span style={{ width: 56, height: 56, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'radial-gradient(circle at 40% 30%, #1b2d52, #07142d 70%)', boxShadow: '0 0 0 1px rgba(205,155,80,.45) inset', flexShrink: 0 }}><PerlaOwl size={48} track={false} /></span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900 }}>Ask the CORE Tutor</div>
                    <div style={{ fontSize: '0.84rem', color: BRAND.muted, lineHeight: 1.45 }}>Lesson plans, answer keys, how to explain a concept to 12-year-olds — the owl in the corner knows the whole course.</div>
                  </div>
                </Card>
              </motion.section>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
